import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useStore } from "../../context/StoreContext";
import { img, money } from "../../lib/api";

export default function CartDrawer() {
  const { cartOpen, setCartOpen, cart, updateQty, removeFromCart, cartSubtotal, settings } = useStore();
  const nav = useNavigate();
  const threshold = settings.free_shipping_threshold || 999;
  const remaining = Math.max(0, threshold - cartSubtotal);
  const progress = Math.min(100, (cartSubtotal / threshold) * 100);

  return (
    <AnimatePresence>
      {cartOpen && (
        <motion.div className="fixed inset-0 z-[70]" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-forest/40" onClick={() => setCartOpen(false)} />
          <motion.div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-ivory flex flex-col"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            data-testid="cart-drawer">
            <div className="flex items-center justify-between px-6 py-5 border-b border-sage/30">
              <span className="serif text-2xl">Your Cart</span>
              <button onClick={() => setCartOpen(false)} data-testid="cart-close"><X size={22} strokeWidth={1.4} /></button>
            </div>

            {cart.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6 text-center">
                <p className="serif text-3xl text-forest/70">Your ritual is waiting.</p>
                <button onClick={() => { setCartOpen(false); nav("/shop"); }} className="btn-primary mt-2" data-testid="cart-empty-shop">Explore Collection</button>
              </div>
            ) : (
              <>
                <div className="px-6 py-4 border-b border-sage/20">
                  {remaining > 0 ? (
                    <p className="text-xs text-forest/70 mb-2">You're {money(remaining)} away from free shipping</p>
                  ) : (
                    <p className="text-xs text-forest mb-2">You've unlocked free shipping.</p>
                  )}
                  <div className="h-1 bg-sage/30"><div className="h-full bg-forest transition-all duration-500" style={{ width: `${progress}%` }} /></div>
                </div>
                <div className="flex-1 overflow-auto px-6 divide-y divide-sage/15">
                  {cart.map((it) => (
                    <div key={it.slug} className="flex gap-4 py-5" data-testid={`cart-item-${it.slug}`}>
                      <img src={img(it.image)} alt={it.name} className="h-24 w-20 object-cover bg-beige/40" />
                      <div className="flex-1">
                        <div className="serif text-lg leading-tight">{it.name}</div>
                        <div className="text-xs text-forest/50 mb-2">{it.size}</div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center border border-forest/20">
                            <button onClick={() => updateQty(it.slug, it.quantity - 1)} className="px-2.5 py-1.5" data-testid={`cart-dec-${it.slug}`}><Minus size={13} /></button>
                            <span className="px-3 text-sm">{it.quantity}</span>
                            <button onClick={() => updateQty(it.slug, it.quantity + 1)} className="px-2.5 py-1.5" data-testid={`cart-inc-${it.slug}`}><Plus size={13} /></button>
                          </div>
                          <span className="text-sm font-medium">{money(it.price * it.quantity)}</span>
                        </div>
                      </div>
                      <button onClick={() => removeFromCart(it.slug)} className="text-forest/40 hover:text-forest self-start" data-testid={`cart-remove-${it.slug}`}><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
                <div className="px-6 py-5 border-t border-sage/30 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-forest/70">Subtotal</span>
                    <span className="serif text-2xl">{money(cartSubtotal)}</span>
                  </div>
                  <button onClick={() => { setCartOpen(false); nav("/checkout"); }} className="btn-primary w-full" data-testid="cart-checkout">Proceed to Checkout</button>
                  <button onClick={() => { setCartOpen(false); nav("/cart"); }} className="w-full text-xs uppercase tracking-[0.16em] text-forest/60 hover:text-forest">View Cart</button>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
